import { useCallback } from 'react';
import { create } from 'zustand';
import {
  loadListViewPrefs,
  saveListViewPrefs,
  boardListPrefs,
  withCollapsed,
  withSort,
} from '../../extensions/bundled/task-manager/listViewPrefs';

/**
 * The List view's per-device state (collapsed sections + sort, cards 6.1 /
 * 6.3) as a small store, so the sort picker in the filter row and the list
 * below it read the same value. Persisted through listViewPrefs.js
 * (localStorage, never synced).
 */
export const useListViewPrefsStore = create((set) => ({
  prefs: loadListViewPrefs(),
  setCollapsed: (boardId, groupId, collapsed) =>
    set((s) => {
      const prefs = withCollapsed(s.prefs, boardId, groupId, collapsed);
      if (prefs === s.prefs) return {};
      saveListViewPrefs(prefs);
      return { prefs };
    }),
  setSort: (boardId, sort) =>
    set((s) => {
      const prefs = withSort(s.prefs, boardId, sort);
      saveListViewPrefs(prefs);
      return { prefs };
    }),
}));

/** `{ sort, setSort, collapsed:Set, toggleCollapsed(groupId) }` for one board. */
export default function useListViewPrefs(boardId) {
  const entry = useListViewPrefsStore((s) => boardListPrefs(s.prefs, boardId));
  const setSortRaw = useListViewPrefsStore((s) => s.setSort);
  const setCollapsedRaw = useListViewPrefsStore((s) => s.setCollapsed);
  const setSort = useCallback((sort) => setSortRaw(boardId, sort), [boardId, setSortRaw]);
  const collapsed = entry.collapsed;
  const toggleCollapsed = useCallback(
    (groupId) => setCollapsedRaw(boardId, groupId, !collapsed.includes(groupId)),
    [boardId, collapsed, setCollapsedRaw]
  );
  return { sort: entry.sort, setSort, collapsed, toggleCollapsed };
}
